import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, MessageSquare } from 'lucide-react';
import './Contact.css';
import { whatsappNumber } from '../data/mock';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Using FormSubmit.co - free email service
    const form = e.target;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://formsubmit.co/dataclouds.ai@gmail.com', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        toast({
          title: "Message Sent Successfully!",
          description: `Thank you ${formData.get('name')}! We'll get back to you within 24 hours.`,
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: ''
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please email us directly at dataclouds.ai@gmail.com",
        variant: "destructive"
      });
    }
    
    setIsSubmitting(false);
  };

  return (
    <div className="contact-page">
      {/* Hero */}
      <section className="contact-hero">
        <div className="container text-center">
          <h1 className="display-large mb-3 fade-in">
            Let's Start Building <span className="gradient-text">Your Success Story</span>
          </h1>
          <p className="body-large fade-in" style={{ animationDelay: '0.1s' }}>
            Ready to transform your business? Get in touch with our team for a free consultation.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Info */}
            <div className="contact-info fade-in">
              <h2 className="heading-1 mb-4">Get In Touch</h2>
              <p className="body-medium mb-4">
                Have a project in mind? We'd love to hear from you. Send us a message and we'll respond within 24 hours.
              </p>

              <div className="contact-methods">
                <div className="contact-method">
                  <div className="method-icon">
                    <Mail size={24} />
                  </div>
                  <div>
                    <div className="method-label">Email Us</div>
                    <a href="mailto:dataclouds.ai@gmail.com" target="_blank" rel="noopener noreferrer" className="method-value">
                      dataclouds.ai@gmail.com
                    </a>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="method-icon">
                    <Phone size={24} />
                  </div>
                  <div>
                    <div className="method-label">Call or WhatsApp</div>
                    <a 
                      href={`https://wa.me/${whatsappNumber.replace(/\+/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="method-value"
                    >
                      {whatsappNumber}
                    </a>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="method-icon">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <div className="method-label">Global Services</div>
                    <p className="method-value">25+ Countries Worldwide</p>
                  </div>
                </div>
              </div>

              <div className="whatsapp-cta glass-card mt-4">
                <MessageSquare size={32} className="whatsapp-icon" />
                <div>
                  <h4 className="heading-3">Prefer WhatsApp?</h4>
                  <p className="body-small mb-3">Get instant responses to your queries</p>
                  <a 
                    href={`https://wa.me/${whatsappNumber.replace(/\+/g, '')}?text=Hi, I'd like to know more about Data Clouds services`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary"
                  >
                    Chat on WhatsApp
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-wrapper fade-in" style={{ animationDelay: '0.15s' }}>
              <form onSubmit={handleSubmit} className="contact-form glass-card" method="POST">
                <h3 className="heading-2 mb-4">Send Us a Message</h3>

                {/* Hidden fields for FormSubmit */}
                <input type="hidden" name="_subject" value="New Contact from Data Clouds Website" />
                <input type="hidden" name="_captcha" value="false" />
                <input type="text" name="_honey" style={{display: 'none'}} />

                <div className="form-group">
                  <label htmlFor="name" className="form-label">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="form-input"
                    required
                    placeholder="John Doe"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-input"
                    required
                    placeholder="john@company.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="company" className="form-label">Company Name</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Your Company"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="service" className="form-label">Service Interested In *</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="form-select"
                    required
                  >
                    <option value="">Select a service</option>
                    <option value="website">Website Development</option>
                    <option value="mobile">Mobile Application</option>
                    <option value="ai">AI Agents & Automation</option>
                    <option value="automation">Business Process Automation</option>
                    <option value="analytics">Data Analytics</option>
                    <option value="marketing">Digital Marketing</option>
                    <option value="video">Video Advertising</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">Project Details *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="form-textarea"
                    rows="5"
                    required
                    placeholder="Tell us about your project, goals, and any specific requirements..."
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  className="btn-primary btn-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send size={20} />
                </button>

                <p className="form-note body-small">
                  By submitting this form, you agree to our privacy policy. We'll never share your information.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section faq-section">
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2 className="display-medium mb-2">Frequently Asked Questions</h2>
            <p className="body-large">Quick answers to common questions</p>
          </div>
          <div className="faq-grid">
            <div className="faq-card glass-card">
              <h4 className="heading-3 mb-2">How long does a typical project take?</h4>
              <p className="body-medium">
                Project timelines vary based on complexity. Websites take 4-6 weeks, mobile apps 8-12 weeks, and enterprise solutions 3-6 months. We'll provide a detailed timeline after our discovery phase.
              </p>
            </div>
            <div className="faq-card glass-card">
              <h4 className="heading-3 mb-2">How do you approach new projects?</h4>
              <p className="body-medium">
                Every project is unique. We start with a discovery phase to understand your needs, goals, and challenges, then create a customized solution tailored to deliver maximum value for your business.
              </p>
            </div>
            <div className="faq-card glass-card">
              <h4 className="heading-3 mb-2">Do you provide ongoing support?</h4>
              <p className="body-medium">
                Yes! We offer comprehensive maintenance packages including updates, security patches, performance optimization, and 24/7 support to ensure long-term success.
              </p>
            </div>
            <div className="faq-card glass-card">
              <h4 className="heading-3 mb-2">Can you work with our existing systems?</h4>
              <p className="body-medium">
                Absolutely. We specialize in integrating with existing infrastructure and can work with any technology stack to enhance your current systems.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
